import 'src/styles/document-viewer.css';

import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { pdfjs } from 'react-pdf';
import DocxViewer from 'src/components/document-viewer/DocxViewer';
import ImageViewer from 'src/components/document-viewer/ImageViewer';
import PdfViewer from 'src/components/document-viewer/PdfViewer';
import Toolbar from 'src/components/document-viewer/Toolbar';
import { isDocxUrl, isImageUrl, isPdfUrl } from 'src/utils/viewableDocument';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const DocumentViewerModal = ({ isOpen, onClose, url, name }) => {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [fitToWidth, setFitToWidth] = useState(true);
  const [scale, setScale] = useState(1);
  const [pageRenderWidth, setPageRenderWidth] = useState(undefined);
  const [docReady, setDocReady] = useState(false);
  const twRef = useRef(null);

  const handleLoadSuccess = useCallback(({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setDocReady(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const updateWidth = () => {
      if (!containerRef.current) return;
      const padding = 24;
      const width = containerRef.current.clientWidth - padding * 2;
      setPageRenderWidth(width > 320 ? width : 320);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setPageNumber(1);
    setScale(1);
    setDocReady(false);
  }, [isOpen, url]);

  const handleZoomIn = () => {
    if (fitToWidth) {
      setFitToWidth(false);
      setScale(1.2);
    } else {
      const api = twRef.current;
      if (api && typeof api.zoomIn === 'function') {
        api.zoomIn(0.2);
      } else {
        setScale(s => Math.min(3, Number((s + 0.2).toFixed(2))));
      }
    }
  };

  const handleZoomOut = () => {
    if (fitToWidth) {
      setFitToWidth(false);
      setScale(0.8);
    } else {
      const api = twRef.current;
      if (api && typeof api.zoomOut === 'function') {
        api.zoomOut(0.2);
      } else {
        setScale(s => Math.max(0.5, Number((s - 0.2).toFixed(2))));
      }
    }
  };

  const handleFit = () => {
    setFitToWidth(v => !v);
    const api = twRef.current;
    if (api && typeof api.resetTransform === 'function') {
      api.resetTransform();
    }
  };

  const openExternal = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const unsupported = !isPdfUrl(url || name) && !isImageUrl(url || name) && !isDocxUrl(url || name);

  const renderDocumentType = urlOrName => {
    if (unsupported) {
      return (
        <div className='dv-unsupported'>
          <p>This file type is not supported for inline preview.</p>
          <button type='button' className='btn btn-primary' onClick={openExternal}>
            Open in new tab
          </button>
        </div>
      );
    }
    if (isPdfUrl(urlOrName)) {
      return (
        <PdfViewer
          url={url}
          pageNumber={pageNumber}
          numPages={numPages}
          fitToWidth={fitToWidth}
          scale={scale}
          pageRenderWidth={pageRenderWidth}
          docReady={docReady}
          onPageChange={setPageNumber}
          onLoadSuccess={handleLoadSuccess}
        />
      );
    }
    if (isImageUrl(urlOrName)) {
      return (
        <ImageViewer
          url={url}
          name={name}
          fitToWidth={fitToWidth}
          pageRenderWidth={pageRenderWidth}
          docReady={docReady}
          transformRef={twRef}
          onReady={() => setDocReady(true)}
        />
      );
    }
    if (isDocxUrl(urlOrName)) {
      return <DocxViewer url={url} />;
    }
  };

  return (
    <ReactModal
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      className={{ base: 'dv-modal', afterOpen: 'dv-modal-open', beforeClose: 'dv-modal-close' }}
      overlayClassName={{
        base: 'dv-overlay',
        afterOpen: 'dv-overlay-open',
        beforeClose: 'dv-overlay-close',
      }}
      closeTimeoutMS={180}
      ariaHideApp={false}
      onRequestClose={onClose}
    >
      <div ref={containerRef} className='dv-container'>
        <Toolbar
          title={name}
          fitToWidth={fitToWidth}
          showZoomControls={!unsupported && !isDocxUrl(url || name)}
          onClose={onClose}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFit={handleFit}
          onExternal={openExternal}
        />
        <div className='dv-body'>{renderDocumentType(url || name)}</div>
      </div>
    </ReactModal>
  );
};

DocumentViewerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  url: PropTypes.string,
  name: PropTypes.string,
};

DocumentViewerModal.defaultProps = {
  url: null,
  name: null,
};

export default DocumentViewerModal;
